import { useState, useEffect } from 'react';
import { graphqlClient } from '@/services/core/graphqlClient';
import { useAuth } from '@/contexts/AuthContext';

export interface ProgramTypeDetail {
  program_type_id: string;
  program_type: string;
  progrma_status: string[];
  program_status_mapper_ids: number[];
  created_datetime: string;
}

export interface UseProgramTypeByIdResult {
  programType: ProgramTypeDetail | null;
  loading: boolean;
  error: string | null;
}

const GET_PROGRAM_TYPE_BY_ID_QUERY = `
  query GetProgramTypeById($programTypeId: Int!) {
    pep_vw_program_type_dashboard(where: { program_type_id: { _eq: $programTypeId } }) {
      program_type
      progrma_status
      program_type_id
      created_datetime
    }
  }
`;

const GET_ALL_PROGRAM_STATUSES_QUERY = `
  query GetAllProgramStatuses {
    pep_program_status {
      program_status_id
      program_status
    }
  }
`;

export const useProgramTypeById = (programTypeId: string | undefined): UseProgramTypeByIdResult => {
  const { hasuraToken } = useAuth();
  const [programType, setProgramType] = useState<ProgramTypeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProgramType = async () => {
      if (!hasuraToken || !programTypeId) {
        setError('Authentication required or invalid ID');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // First, fetch the program type details
        const programTypeResponse = await graphqlClient.query(
          GET_PROGRAM_TYPE_BY_ID_QUERY,
          { programTypeId: parseInt(programTypeId) },
          hasuraToken
        );

        if (!programTypeResponse.success || !programTypeResponse.data?.pep_vw_program_type_dashboard?.[0]) {
          setError('Program type not found');
          setProgramType(null);
          setLoading(false);
          return;
        }

        const programTypeData = programTypeResponse.data.pep_vw_program_type_dashboard[0];
        
        // Fetch all program statuses to map names to IDs
        const statusesResponse = await graphqlClient.query(
          GET_ALL_PROGRAM_STATUSES_QUERY,
          {},
          hasuraToken
        );

        const allStatuses = statusesResponse.success && statusesResponse.data?.pep_program_status
          ? statusesResponse.data.pep_program_status
          : [];
        
        // Match status names from progrma_status to program_status_id from pep_program_status
        const selectedStatusNames = programTypeData.progrma_status || [];
        const statusIds = allStatuses
          .filter((s: any) => selectedStatusNames.includes(s.program_status))
          .map((s: any) => s.program_status_id);

        // Combine the data
        setProgramType({
          ...programTypeData,
          program_status_mapper_ids: statusIds
        });
        setError(null);
      } catch (err: any) {
        console.error('Error fetching program type:', err);
        setError(err.message || 'Failed to fetch program type');
        setProgramType(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProgramType();
  }, [hasuraToken, programTypeId]);

  return {
    programType,
    loading,
    error
  };
};
